import React, { useState, useEffect, useMemo } from 'react';
import { useFloatMessage, MESSAGE_TYPE } from '../../Providers/FloatMessage';
import { UpdatePrizeQueueBL } from '../../../utilities/BL/PersonalRoulette';

/**
 * MODAL / DRAWER DE EDICIÓN RÁPIDA EN CALIENTE DE LA FILA DE PREMIOS (HU-07)
 * Permite al operador reordenar (subir/bajar), agregar y remover pasos de la secuencia
 * de premiación sin interrumpir la dinámica en tiempo real.
 */
export const PersonalRouletteQueueModal = ({
    isOpen,
    onClose,
    roomId = null,
    config = null,
    onQueueUpdated = null
}) => {
    const { showMessage } = useFloatMessage();

    // Lista editable de IDs o pasos de la fila
    const [queueItems, setQueueItems] = useState([]);
    const [selectedPrizeToAdd, setSelectedPrizeToAdd] = useState('');
    const [isSaving, setIsSaving] = useState(false);

    // Mapeo indexado de premios para resolución rápida
    const prizesMap = useMemo(() => {
        const map = new Map();
        if (config?.prizes && Array.isArray(config.prizes)) {
            config.prizes.forEach(p => {
                map.set(String(p.id), p);
            });
        }
        return map;
    }, [config]);

    // Lista de premios ganadores configurados
    const availableWinnerPrizes = useMemo(() => {
        if (!config?.prizes || !Array.isArray(config.prizes)) return [];
        return config.prizes.filter(p => p.isWinner);
    }, [config]);

    // Sincronizar estado al abrir modal
    useEffect(() => {
        if (!isOpen) return;

        let initialQueue = [];
        if (config?.prizeQueue && Array.isArray(config.prizeQueue) && config.prizeQueue.length > 0) {
            initialQueue = config.prizeQueue.map(item => {
                if (typeof item === 'string') return item;
                return item?.prizeId || item?.id || '';
            }).filter(Boolean);
        } else if (availableWinnerPrizes.length > 0) {
            // Si la fila está vacía, pre-cargar con premios ganadores con stock disponible
            initialQueue = availableWinnerPrizes
                .filter(p => (Number(p.totalStock) || 0) - (Number(p.awardedCount) || 0) > 0)
                .map(p => String(p.id));
        }

        setQueueItems(initialQueue);

        // Seleccionar primer premio ganador disponible por defecto
        const firstAvailable = availableWinnerPrizes.find(p => (Number(p.totalStock) || 0) - (Number(p.awardedCount) || 0) > 0);
        setSelectedPrizeToAdd(firstAvailable ? String(firstAvailable.id) : (availableWinnerPrizes[0] ? String(availableWinnerPrizes[0].id) : ''));
    }, [isOpen, config, availableWinnerPrizes]);

    // Mover paso arriba
    const handleMoveUp = (index) => {
        if (index <= 0) return;
        setQueueItems(prev => {
            const next = [...prev];
            const temp = next[index - 1];
            next[index - 1] = next[index];
            next[index] = temp;
            return next;
        });
    };

    // Mover paso abajo
    const handleMoveDown = (index) => {
        if (index >= queueItems.length - 1) return;
        setQueueItems(prev => {
            const next = [...prev];
            const temp = next[index + 1];
            next[index + 1] = next[index];
            next[index] = temp;
            return next;
        });
    };

    // Remover paso de la fila
    const handleRemoveItem = (index) => {
        if (queueItems.length <= 1) {
            showMessage({
                type: MESSAGE_TYPE.BAD,
                text: 'La fila debe contener al menos 1 paso de premiación.'
            });
            return;
        }
        setQueueItems(prev => prev.filter((_, idx) => idx !== index));
    };

    // Añadir premio seleccionado a la fila
    const handleAddPrizeToQueue = () => {
        if (!selectedPrizeToAdd) {
            showMessage({
                type: MESSAGE_TYPE.BAD,
                text: 'Selecciona un premio para agregar a la fila.'
            });
            return;
        }

        const prizeObj = prizesMap.get(String(selectedPrizeToAdd));
        if (prizeObj) {
            const remaining = (Number(prizeObj.totalStock) || 0) - (Number(prizeObj.awardedCount) || 0);
            if (remaining <= 0) {
                showMessage({
                    type: MESSAGE_TYPE.BAD,
                    text: `El premio "${prizeObj.textEs || 'Premio'}" está agotado (Stock: 0).`
                });
                return;
            }
        }

        setQueueItems(prev => [...prev, String(selectedPrizeToAdd)]);
        showMessage({
            type: MESSAGE_TYPE.SUCCESS,
            text: 'Paso agregado a la fila.'
        });
    };

    // Preajuste: Poblar con todos los premios ganadores con stock
    const handleAutoPopulateWinners = () => {
        const winnerIds = availableWinnerPrizes
            .filter(p => (Number(p.totalStock) || 0) - (Number(p.awardedCount) || 0) > 0)
            .map(p => String(p.id));

        if (winnerIds.length === 0) {
            showMessage({
                type: MESSAGE_TYPE.BAD,
                text: 'No hay premios ganadores con stock disponible.'
            });
            return;
        }

        setQueueItems(winnerIds);
        showMessage({
            type: MESSAGE_TYPE.SUCCESS,
            text: `Fila rellenada con ${winnerIds.length} premios disponibles.`
        });
    };

    // Preajuste: Duplicar ciclo actual
    const handleDuplicateCycle = () => {
        if (queueItems.length === 0) return;
        if (queueItems.length >= 30) {
            showMessage({
                type: MESSAGE_TYPE.BAD,
                text: 'La fila ya tiene un ciclo extenso (máximo 30 pasos recomendados).'
            });
            return;
        }
        setQueueItems(prev => [...prev, ...prev]);
        showMessage({
            type: MESSAGE_TYPE.SUCCESS,
            text: 'Ciclo de la fila duplicado.'
        });
    };

    // Guardar cambios en el backend
    const handleSaveQueue = async () => {
        if (queueItems.length === 0) {
            showMessage({
                type: MESSAGE_TYPE.BAD,
                text: 'La fila de premios no puede estar vacía.'
            });
            return;
        }

        setIsSaving(true);
        try {
            const res = await UpdatePrizeQueueBL(roomId, queueItems);
            if (res.success) {
                showMessage({
                    type: MESSAGE_TYPE.SUCCESS,
                    text: '¡Fila de premios actualizada y sincronizada en caliente!'
                });
                if (onQueueUpdated) onQueueUpdated(queueItems);
                if (onClose) onClose();
            } else {
                showMessage({
                    type: MESSAGE_TYPE.BAD,
                    text: res.message || 'Error al guardar la fila de premios.'
                });
            }
        } catch (err) {
            showMessage({
                type: MESSAGE_TYPE.BAD,
                text: 'Error de red al actualizar la fila de premios.'
            });
        } finally {
            setIsSaving(false);
        }
    };

    if (!isOpen) return null;

    const currentIndex = Number(config?.currentQueueIndex) || 0;

    return (
        <>
            {/* Backdrop */}
            <div
                onClick={onClose}
                style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    width: '100vw',
                    height: '100vh',
                    backgroundColor: 'rgba(0, 0, 0, 0.75)',
                    zIndex: 110000,
                    backdropFilter: 'blur(4px)'
                }}
            />

            {/* Modal Box */}
            <div
                style={{
                    position: 'fixed',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    width: '95%',
                    maxWidth: '680px',
                    maxHeight: '92vh',
                    zIndex: 110001,
                    display: 'flex',
                    flexDirection: 'column'
                }}
                className="PersonalRouletteQueueModal"
            >
                <div className="card shadow-lg border-0 rounded-4 overflow-hidden text-dark bg-white d-flex flex-column h-100">
                    {/* Header */}
                    <div className="bg-dark p-3 px-4 d-flex justify-content-between align-items-center text-white flex-shrink-0 border-bottom border-warning border-3">
                        <div className="d-flex align-items-center gap-2">
                            <div className="rounded-3 bg-warning text-dark p-2 d-flex align-items-center justify-content-center" style={{ width: '38px', height: '38px' }}>
                                <i className="bi bi-list-ol fs-5 fw-bold"></i>
                            </div>
                            <div>
                                <h5 className="mb-0 fw-bold text-white">Edición en Caliente: Fila de Premios</h5>
                                <small className="text-warning">Reordena o modifica la secuencia paso a paso en tiempo real</small>
                            </div>
                        </div>
                        <button
                            type="button"
                            className="btn-close btn-close-white"
                            onClick={onClose}
                            disabled={isSaving}
                        ></button>
                    </div>

                    {/* Barra de Acciones Rápidas */}
                    <div className="bg-light p-3 border-bottom d-flex flex-wrap justify-content-between align-items-center gap-2 flex-shrink-0">
                        <div className="d-flex align-items-center gap-1">
                            <span className="badge bg-dark font-monospace px-2 py-1">
                                {queueItems.length} {queueItems.length === 1 ? 'Paso' : 'Pasos'} en Fila
                            </span>
                            <span className="badge bg-warning-subtle text-dark border border-warning">
                                En turno: Paso #{((currentIndex) % Math.max(1, queueItems.length)) + 1}
                            </span>
                        </div>

                        <div className="d-flex align-items-center gap-1.5">
                            <button
                                type="button"
                                className="btn btn-sm btn-outline-secondary d-flex align-items-center gap-1"
                                onClick={handleAutoPopulateWinners}
                                title="Auto-rellenar con todos los premios ganadores con stock disponible"
                            >
                                <i className="bi bi-magic text-warning"></i>
                                <span>Auto-rellenar</span>
                            </button>
                            <button
                                type="button"
                                className="btn btn-sm btn-outline-secondary d-flex align-items-center gap-1"
                                onClick={handleDuplicateCycle}
                                title="Duplicar el patrón actual"
                            >
                                <i className="bi bi-copy"></i>
                                <span>Duplicar Ciclo</span>
                            </button>
                        </div>
                    </div>

                    {/* Selector para Agregar Nuevo Paso */}
                    <div className="p-3 bg-white border-bottom flex-shrink-0">
                        <label className="form-label small fw-bold text-secondary mb-1">
                            <i className="bi bi-plus-circle me-1 text-danger"></i> Agregar Premio a la Fila:
                        </label>
                        <div className="input-group">
                            <select
                                className="form-select"
                                value={selectedPrizeToAdd}
                                onChange={(e) => setSelectedPrizeToAdd(e.target.value)}
                            >
                                {availableWinnerPrizes.length === 0 ? (
                                    <option value="" disabled>No hay premios ganadores configurados</option>
                                ) : (
                                    availableWinnerPrizes.map((p) => {
                                        const stock = Number(p.totalStock) || 0;
                                        const awarded = Number(p.awardedCount) || 0;
                                        const remaining = Math.max(0, stock - awarded);
                                        const isExhausted = remaining <= 0;

                                        return (
                                            <option
                                                key={p.id}
                                                value={p.id}
                                                disabled={isExhausted}
                                                className={isExhausted ? 'text-danger' : ''}
                                            >
                                                {p.textEs || p.textEn || 'Premio'} — {isExhausted ? '❌ AGOTADO (0 u)' : `🟢 ${remaining}/${stock} disp.`}
                                            </option>
                                        );
                                    })
                                )}
                            </select>
                            <button
                                type="button"
                                className="btn btn-danger fw-bold d-flex align-items-center gap-1.5 px-3"
                                onClick={handleAddPrizeToQueue}
                                disabled={!selectedPrizeToAdd}
                            >
                                <i className="bi bi-plus-lg"></i>
                                <span>Añadir</span>
                            </button>
                        </div>
                    </div>

                    {/* Lista de Pasos con Scroll */}
                    <div className="card-body p-3 overflow-auto flex-grow-1" style={{ maxHeight: 'calc(80vh - 230px)', backgroundColor: '#f8fafc' }}>
                        {queueItems.length === 0 ? (
                            <div className="text-center py-5 text-muted">
                                <i className="bi bi-inbox fs-1 d-block mb-2 text-secondary opacity-50"></i>
                                <p className="fw-bold mb-1">Fila de premios vacía</p>
                                <p className="small mb-0">Agrega premios desde el selector superior o usa "Auto-rellenar".</p>
                            </div>
                        ) : (
                            <div className="d-flex flex-column gap-2">
                                {queueItems.map((prizeId, idx) => {
                                    const prize = prizesMap.get(String(prizeId));
                                    const isCurrent = idx === (currentIndex % queueItems.length);
                                    const stock = Number(prize?.totalStock) || 0;
                                    const awarded = Number(prize?.awardedCount) || 0;
                                    const remaining = Math.max(0, stock - awarded);
                                    const isExhausted = remaining <= 0;

                                    return (
                                        <div
                                            key={`${prizeId}_${idx}`}
                                            className={`card border rounded-3 shadow-sm transition-all ${
                                                isCurrent ? 'border-warning bg-warning-subtle shadow-md' : 'bg-white'
                                            } ${isExhausted ? 'opacity-75 bg-light' : ''}`}
                                            style={{
                                                borderLeftWidth: isCurrent ? '6px' : '4px',
                                                borderLeftColor: isCurrent ? '#f59e0b' : (prize?.backgroundColor || '#dc2626')
                                            }}
                                        >
                                            <div className="card-body p-2 px-3 d-flex justify-content-between align-items-center gap-2">
                                                {/* Paso & Premio */}
                                                <div className="d-flex align-items-center gap-2.5 flex-grow-1 text-truncate">
                                                    <span className={`badge ${isCurrent ? 'bg-warning text-dark fw-bold' : 'bg-dark text-white'} rounded-pill font-monospace`}>
                                                        #{idx + 1}
                                                    </span>

                                                    {/* Color e Icono */}
                                                    <div
                                                        className="rounded-circle d-flex align-items-center justify-content-center text-white shadow-xs flex-shrink-0"
                                                        style={{
                                                            width: '32px',
                                                            height: '32px',
                                                            backgroundColor: prize?.backgroundColor || '#dc2626'
                                                        }}
                                                    >
                                                        {prize?.iconUrl ? (
                                                            <img
                                                                src={prize.iconUrl}
                                                                alt={prize.textEs || 'Icono'}
                                                                style={{ width: '20px', height: '20px', objectFit: 'contain' }}
                                                            />
                                                        ) : (
                                                            <i className="bi bi-trophy-fill" style={{ fontSize: '0.85rem' }}></i>
                                                        )}
                                                    </div>

                                                    {/* Nombre y Estado */}
                                                    <div className="text-truncate">
                                                        <div className="d-flex align-items-center gap-1.5 flex-wrap">
                                                            <span className="fw-bold text-dark text-truncate">
                                                                {prize?.textEs || prize?.textEn || `Premio (${prizeId})`}
                                                            </span>
                                                            {isCurrent && (
                                                                <span className="badge bg-warning text-dark border border-dark-subtle fw-bold" style={{ fontSize: '0.68rem' }}>
                                                                    👉 EN TURNO
                                                                </span>
                                                            )}
                                                            {isExhausted ? (
                                                                <span className="badge bg-danger text-white fw-bold" style={{ fontSize: '0.68rem' }}>
                                                                    ❌ Agotado (0 u)
                                                                </span>
                                                            ) : (
                                                                <span className="badge bg-success-subtle text-success border border-success" style={{ fontSize: '0.68rem' }}>
                                                                    {remaining}/{stock} disp.
                                                                </span>
                                                            )}
                                                        </div>
                                                        <small className="text-muted d-block text-truncate" style={{ fontSize: '0.72rem' }}>
                                                            {prize?.descriptionEs || prize?.descriptionEn || 'Sector oficial'}
                                                        </small>
                                                    </div>
                                                </div>

                                                {/* Controles de Ordenamiento */}
                                                <div className="d-flex align-items-center gap-1 flex-shrink-0">
                                                    <button
                                                        type="button"
                                                        className="btn btn-sm btn-outline-secondary py-1 px-2"
                                                        onClick={() => handleMoveUp(idx)}
                                                        disabled={idx === 0 || isSaving}
                                                        title="Mover arriba en la fila"
                                                    >
                                                        <i className="bi bi-arrow-up"></i>
                                                    </button>
                                                    <button
                                                        type="button"
                                                        className="btn btn-sm btn-outline-secondary py-1 px-2"
                                                        onClick={() => handleMoveDown(idx)}
                                                        disabled={idx === queueItems.length - 1 || isSaving}
                                                        title="Mover abajo en la fila"
                                                    >
                                                        <i className="bi bi-arrow-down"></i>
                                                    </button>
                                                    <button
                                                        type="button"
                                                        className="btn btn-sm btn-outline-danger py-1 px-2 ms-1"
                                                        onClick={() => handleRemoveItem(idx)}
                                                        disabled={queueItems.length <= 1 || isSaving}
                                                        title="Eliminar este paso de la fila"
                                                    >
                                                        <i className="bi bi-trash"></i>
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </div>

                    {/* Footer con Acciones */}
                    <div className="card-footer bg-light p-3 px-4 d-flex justify-content-between align-items-center border-top flex-shrink-0">
                        <button
                            type="button"
                            className="btn btn-outline-secondary px-3"
                            onClick={onClose}
                            disabled={isSaving}
                        >
                            Cancelar
                        </button>

                        <button
                            type="button"
                            className="btn btn-danger px-4 fw-bold text-white d-flex align-items-center gap-2 shadow-sm"
                            onClick={handleSaveQueue}
                            disabled={isSaving || queueItems.length === 0}
                        >
                            {isSaving ? (
                                <>
                                    <span className="spinner-border spinner-border-sm" role="status"></span>
                                    <span>Sincronizando Fila...</span>
                                </>
                            ) : (
                                <>
                                    <i className="bi bi-check-lg"></i>
                                    <span>Guardar Fila</span>
                                </>
                            )}
                        </button>
                    </div>
                </div>
            </div>
        </>
    );
};

export default PersonalRouletteQueueModal;
